import React, { Component } from 'react'
import { Form, Formik } from 'formik'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { ButtonMedium, media } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import { H4, Text } from 'app/common/typography'
import { Error, Password } from 'app/common/forms'
import { object, ref, string } from 'yup'
import { regex } from 'shared/utils'
import onClickOutside from 'react-onclickoutside'
import { ModalTitle } from 'app/common/modals'
import type { ChangePasswordForm } from 'shared/types'

type Props = {
    updatePassword: ChangePasswordForm => void,
    resetPasswordSuccess: () => void,
    updatePasswordSuccess: boolean,
    updatePasswordError: ?string,
    handleClose: () => void
}

class UpdatePasswordForm extends Component<Props> {
    /* Manage clickOutside Modal to close it */
    handleClickOutside(e) {
        const { handleClose } = this.props
        handleClose()
    }

    componentDidUpdate() {
        const { handleClose, updatePasswordSuccess } = this.props
        if (updatePasswordSuccess) {
            handleClose()
        }
    }

    formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        return object().shape({
            previous: string().required(mandatory),
            newPassword: string()
                .required(mandatory)
                .matches(regex.PASSWORD, {
                    message: 'form.error.regex.password',
                    excludeEmptyString: true
                }),
            confirmedPassword: string()
                .required(mandatory)
                .oneOf(
                    [ref('newPassword'), null],
                    'form.password.update.match.error'
                )
        })
    }

    render() {
        const {
            handleClose,
            updatePassword,
            updatePasswordError,
            resetPasswordSuccess
        } = this.props
        const { red, yellow } = colors
        const initialValues: ChangePasswordForm = {
            previous: '',
            newPassword: '',
            confirmedPassword: ''
        }

        return (
            <Content>
                <ModalTitle
                    center
                    handleClose={handleClose}
                    titleNode={
                        <FormattedMessage id="kingdom.profile.form.password.title">
                            {(text: string) => <StyledH4>{text}</StyledH4>}
                        </FormattedMessage>
                    }
                />
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    onSubmit={async (values, actions) => {
                        resetPasswordSuccess()
                        const postedData = {
                            previous: values.previous,
                            newPassword: values.newPassword,
                            confirmedPassword: values.confirmedPassword
                        }
                        try {
                            await updatePassword(postedData)
                        } finally {
                            actions.setSubmitting(false)
                        }
                    }}
                    validationSchema={this.formSubscription}>
                    {({
                        handleChange,
                        handleSubmit,
                        handleBlur,
                        values,
                        errors,
                        touched,
                        isSubmitting
                    }) => (
                        <Form>
                            <Password
                                name="previous"
                                type="password"
                                labelKey="kingdom.profile.form.password.old.title"
                                placeholderKey="kingdom.profile.form.password.old.placeholder"
                                className={
                                    errors.previous && touched.previous
                                        ? 'error'
                                        : ''
                                }
                            />
                            {errors.previous && touched.previous && (
                                <Error errorKey={errors.previous} />
                            )}
                            <Password
                                name="newPassword"
                                type="password"
                                labelKey="kingdom.profile.form.password.new.title"
                                placeholderKey="kingdom.profile.form.password.new.placeholder"
                                className={
                                    errors.newPassword && touched.newPassword
                                        ? 'error'
                                        : ''
                                }
                            />
                            {errors.newPassword && touched.newPassword && (
                                <Error errorKey={errors.newPassword} />
                            )}
                            <Password
                                name="confirmedPassword"
                                type="password"
                                labelKey="kingdom.profile.form.password.confirmed.title"
                                placeholderKey="kingdom.profile.form.password.confirmed.placeholder"
                                className={
                                    errors.confirmedPassword &&
                                    touched.confirmedPassword
                                        ? 'error'
                                        : ''
                                }
                            />
                            {errors.confirmedPassword &&
                                touched.confirmedPassword && (
                                    <Error
                                        errorKey={errors.confirmedPassword}
                                    />
                                )}
                            <ButtonContainer>
                                <StyledButtonMedium
                                    onClick={handleClose}
                                    background={yellow}
                                    disabled={isSubmitting}>
                                    <FormattedMessage id="form.button.cancel" />
                                </StyledButtonMedium>
                                <StyledButtonMedium
                                    type="submit"
                                    background={yellow}
                                    disabled={isSubmitting}>
                                    <FormattedMessage id="kingdom.profile.form.button.save" />
                                </StyledButtonMedium>
                            </ButtonContainer>
                            {updatePasswordError && (
                                <Mentions>
                                    <FormattedMessage id={updatePasswordError}>
                                        {(text: string) => (
                                            <Text color={red}>{text}</Text>
                                        )}
                                    </FormattedMessage>
                                </Mentions>
                            )}
                        </Form>
                    )}
                </Formik>
            </Content>
        )
    }
}

const Content = styled.div`
    padding: 0 20px;
    height: 100vh;
    ${media.medium`
        padding: 40px;
        height: auto;
    `};
`

const StyledH4 = styled(H4)`
    text-align: center;
`

const Mentions = styled.div`
    margin-top: 20px;
    ${props => (props.center ? 'text-align: center' : '')};
`

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 30px;
`

const StyledButtonMedium = styled(ButtonMedium)`
    align-self: center;
    width: calc(50% - 20px);
    border: none;
`

const UpdatePasswordFormComponent = onClickOutside(UpdatePasswordForm)

export { UpdatePasswordFormComponent }
